import React, { useContext, useEffect, useState } from 'react';
import { AiOutlineClose, AiOutlineUser, AiOutlineHeart, AiOutlineHome, AiOutlineShoppingCart } from 'react-icons/ai';
import { MdOutlineCardGiftcard, MdOutlineSettings, MdLogout } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { sendOtp, loginUser, setDummyUser, logout, registerUser } from '../../store/slices/authSlice';
import { fetchRoles } from '../../store/slices/roleSlice';
import { RootState, AppDispatch } from '../../store';
import { syncLocalStorageCart, fetchCartItems } from '../../utils/cartSync';
import { getWishlist } from '../../utils/wishlistSync';
import AddressModal from './AddressModal';
import { toast } from '../../utils/toast';

interface AccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    name: any;
    onOpenOrders?: () => void;
    onOpenCart?: () => void;
    onOpenWishlist?: () => void;
    onOpenSettings?: () => void;
}

const AccountModal = ({ 
    isOpen, 
    onClose, 
    name, 
    onOpenOrders, 
    onOpenCart, 
    onOpenWishlist, 
    onOpenSettings 
}: AccountModalProps) => {
    const { isAuth } = useContext(AuthContext);
    const navigate = useNavigate();
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [wishlistCount, setWishlistCount] = useState(0);
    const [cartCount, setCartCount] = useState(0);

    const updateWishlistCount = () => {
        try {
            setWishlistCount(getWishlist().length);
        } catch (e) {
            console.error("Error updating wishlist count:", e);
        }
    };

    const updateCartCount = async (useLocalStorageOnly = false) => {
        try {
            let items = [];
            if (useLocalStorageOnly) {
                const cartStr = localStorage.getItem('honeywill_cart');
                items = cartStr ? JSON.parse(cartStr) : [];
            } else {
                items = await fetchCartItems();
            }
            const count = items.reduce((acc: number, item: any) => acc + (item.no || item.quantity || 1), 0);
            setCartCount(count);
        } catch (e) {
            console.error("Error updating cart count:", e);
        }
    };

    // OTP Login States
    const [loginStep, setLoginStep] = useState<'welcome' | 'phone' | 'otp' | 'register'>('welcome');
    const [mobileNumber, setMobileNumber] = useState('');
    const [otpValue, setOtpValue] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [roleId, setRoleId] = useState('');

    // Registration States (triggered when OTP status = 'inactive')
    const [pendingToken, setPendingToken] = useState('');
    const [regFirstName, setRegFirstName] = useState('');
    const [regLastName, setRegLastName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regLoading, setRegLoading] = useState(false);
    const [regError, setRegError] = useState('');

    const dispatch = useDispatch<AppDispatch>();
    const { roles } = useSelector((state: RootState) => state.role);

    useEffect(() => {
        if (!isOpen) return;

        updateWishlistCount();
        updateCartCount(false);

        const handleUpdate = () => {
            updateWishlistCount();
            updateCartCount(true);
        };

        window.addEventListener('storage', handleUpdate);
        window.addEventListener('wishlistUpdated', handleUpdate);
        window.addEventListener('cartUpdated', handleUpdate);

        return () => {
            window.removeEventListener('storage', handleUpdate);
            window.removeEventListener('wishlistUpdated', handleUpdate);
            window.removeEventListener('cartUpdated', handleUpdate);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            // Only reset if they open and aren't logged in
            if (!isAuth) {
                setLoginStep('welcome');
                setMobileNumber('');
                setOtpValue('');
                setLoginError('');
            }
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, isAuth]);

    useEffect(() => {
        if (isOpen && !isAuth) {
            dispatch(fetchRoles({ filter: { name: 'Customer' } }));
        }
    }, [isOpen, isAuth, dispatch]);

    useEffect(() => {
        if (roles && roles.length > 0) {
            const customerRole = roles.find(r => r.name === 'Customer') || roles.find(r => r.name === 'User') || roles[0];
            if (customerRole) {
                const targetId = customerRole.role_id || customerRole.role_type || '3';
                setRoleId(String(targetId));
            }
        } else {
            setRoleId('3');
        }
    }, [roles]);



    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginError('');

        if (mobileNumber.length !== 10 || isNaN(Number(mobileNumber))) {
            setLoginError('Please enter a valid 10-digit phone number');
            return;
        }

        setLoginLoading(true);
        try {
            const targetRoleId = roleId || '3';
            const actionResult = await dispatch(sendOtp({ mobile: mobileNumber, roleId: targetRoleId }));

            if (sendOtp.fulfilled.match(actionResult)) {
                toast.success("OTP sent successfully to +91 " + mobileNumber);
                setLoginStep('otp');
            } else {
                const errMsg = actionResult.payload as string || 'Failed to send OTP';
                setLoginError(errMsg);
            }
        } catch (err: any) {
            setLoginError(err.message || 'Error occurred while sending OTP.');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoginError('');

        if (otpValue.length !== 4 || isNaN(Number(otpValue))) {
            setLoginError('Please enter a 4-digit OTP');
            return;
        }

        setLoginLoading(true);
        try {
            const targetRoleId = roleId || '3';
            const actionResult = await dispatch(loginUser({ mobile: mobileNumber, otp: otpValue, roleId: targetRoleId }));

            if (loginUser.fulfilled.match(actionResult)) {
                const responseData = actionResult.payload;

                // If user is inactive → show registration form
                if (responseData?.status === 'inactive') {
                    setPendingToken(responseData?.access_token || '');
                    setRegFirstName('');
                    setRegLastName('');
                    setRegEmail('');
                    setRegError('');
                    setLoginStep('register');
                    return;
                }

                await syncLocalStorageCart();
                toast.success('OTP verified successfully!');
                onClose();
            } else {
                setLoginError((actionResult.payload as string) || 'Invalid OTP');
            }
        } catch (err: any) {
            setLoginError(err.message || 'Verification failed.');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setRegError('');

        if (!regFirstName.trim()) { setRegError('First name is required'); return; }
        if (regEmail.trim() && !/\S+@\S+\.\S+/.test(regEmail.trim())) { setRegError('Please enter a valid email address'); return; }

        setRegLoading(true);
        try {
            const targetRoleId = roleId || '3';
            const actionResult = await dispatch(registerUser({
                first_name: regFirstName.trim(),
                last_name: regLastName.trim(),
                email: regEmail.trim(),
                phone_number: mobileNumber,
                roleId: [3],
            }));

            if (registerUser.fulfilled.match(actionResult)) {
                // If the registration API returns a new token, use it; otherwise use the pending OTP token
                const responseData = actionResult.payload;
                const newToken = responseData?.access_token || pendingToken;
                const { access_token: _a, ...userRest } = responseData || {};
                const newUser = Object.keys(userRest).length > 0 ? userRest : {
                    first_name: regFirstName.trim(),
                    last_name: regLastName.trim(),
                    email: regEmail.trim(),
                    phone_number: mobileNumber,
                    status: 'active',
                    is_active: true,
                };

                dispatch(setDummyUser({ user: newUser, token: newToken }));
                await syncLocalStorageCart();
                toast.success('Registration successful! Welcome to HoneyCool 🎉');
                onClose();
                navigate('/');
            } else {
                setRegError((actionResult.payload as string) || 'Registration failed. Please try again.');
            }
        } catch (err: any) {
            setRegError(err.message || 'Registration failed.');
        } finally {
            setRegLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="account-modal-overlay" onClick={onClose}>
            <style>
                {`
                .account-modal-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    justify-content: flex-end;
                    background-color: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    animation: fadeInOverlay 0.3s ease;
                }

                @keyframes fadeInOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }

                .account-drawer {
                    animation: slideInRight 0.45s cubic-bezier(0.16, 1, 0.3, 1);
                    width: 32% !important;
                    min-width: 440px !important;
                    background-color: #ffffff;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    box-shadow: -15px 0 45px rgba(15, 23, 42, 0.08);
                    border-top-left-radius: 24px;
                    border-bottom-left-radius: 24px;
                    overflow: hidden;
                    position: relative;
                }

                .account-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 24px;
                    border-bottom: 1px solid #f3f4f6;
                    background-color: #ffffff;
                }

                .account-title-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .account-title {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 800;
                    color: #1f2937;
                    letter-spacing: -0.5px;
                }

                .account-close-btn {
                    background: #f3f4f6;
                    border: none;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #4b5563;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .account-close-btn:hover {
                    background-color: #e5e7eb;
                    color: #111827;
                    transform: scale(1.05);
                }

                .account-content {
                    flex: 1;
                    overflow-y: auto;
                    background-color: #fcfcfc;
                    padding: 24px;
                }

                .profile-banner {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 20px;
                    background: linear-gradient(135deg, #f0fbfb 0%, #e6f9fa 100%);
                    border: 1px solid #e6f9fa;
                    border-radius: 16px;
                    margin-bottom: 24px;
                }

                .profile-avatar {
                    width: 56px;
                    height: 56px;
                    border-radius: 14px;
                    background-color: #43c8ca;
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                    font-weight: 800;
                    box-shadow: 0 4px 10px rgba(67, 200, 202, 0.25);
                }

                .profile-name {
                    font-size: 17px;
                    font-weight: 800;
                    color: #1f2937;
                    text-transform: capitalize;
                }

                .profile-email {
                    font-size: 13px;
                    color: #6b7280;
                    margin-top: 2px;
                }

                .account-menu-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .account-menu-item {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 14px 18px;
                    background-color: #ffffff;
                    border-radius: 12px;
                    border: 1px solid #f3f4f6;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    text-decoration: none;
                    color: #4b5563;
                }

                .account-menu-item:hover {
                    background-color: #f0fbfb;
                    color: #43c8ca;
                    border-color: #e6f9fa;
                    transform: translateX(4px);
                }

                .account-menu-icon {
                    font-size: 20px;
                }

                .account-menu-label {
                    font-size: 14px;
                    font-weight: 700;
                    letter-spacing: 0.3px;
                }

                .account-menu-badge {
                    background: #ff4757;
                    color: #ffffff;
                    font-size: 10.5px;
                    font-weight: 850;
                    padding: 2px 8px;
                    border-radius: 12px;
                    margin-left: auto;
                }

                .account-menu-badge.teal {
                    background: #43c8ca;
                }

                /* OTP Form Styling */
                .login-card {
                    background-color: #ffffff;
                    border: 1px solid #f3f4f6;
                    border-radius: 20px;
                    padding: 24px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.01);
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    margin-bottom: 16px;
                    text-align: left;
                }

                .form-label {
                    font-size: 11.5px;
                    font-weight: 800;
                    color: #4b5563;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .input-wrapper {
                    display: flex;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    overflow: hidden;
                    background-color: #f9fafb;
                    transition: all 0.2s;
                }

                .input-wrapper:focus-within {
                    border-color: #43c8ca;
                    background-color: #ffffff;
                    box-shadow: 0 0 0 3px rgba(67, 200, 202, 0.15);
                }

                .input-prefix {
                    background-color: #f3f4f6;
                    padding: 12px 16px;
                    font-size: 14px;
                    font-weight: 700;
                    color: #4b5563;
                    border-right: 1px solid #e5e7eb;
                }

                .form-input {
                    flex: 1;
                    padding: 12px 16px;
                    border: none;
                    outline: none;
                    background: transparent;
                    font-size: 14px;
                    color: #1f2937;
                }

                .form-input:disabled {
                    color: #9ca3af;
                    cursor: not-allowed;
                }

                .otp-input {
                    width: 100%;
                    padding: 12px 16px;
                    border-radius: 12px;
                    border: 1px solid #e5e7eb;
                    outline: none;
                    font-size: 18px;
                    text-align: center;
                    letter-spacing: 8px;
                    font-weight: 800;
                    background-color: #f9fafb;
                    color: #1f2937;
                    transition: all 0.2s;
                }

                .otp-input:focus {
                    border-color: #43c8ca;
                    background-color: #ffffff;
                    box-shadow: 0 0 0 3px rgba(67, 200, 202, 0.15);
                }

                .login-action-btn {
                    width: 100%;
                    padding: 14px;
                    background-color: #43c8ca;
                    color: #ffffff;
                    font-weight: 800;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    font-size: 14px;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                    box-shadow: 0 4px 12px rgba(67, 200, 202, 0.25);
                    transition: all 0.2s;
                }

                .login-action-btn:hover:not(:disabled) {
                    background-color: #3db0b2;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(67, 200, 202, 0.35);
                }

                .login-action-btn:disabled {
                    background-color: #d1d5db;
                    color: #9ca3af;
                    box-shadow: none;
                    cursor: not-allowed;
                }

                .error-box {
                    color: #ef4444;
                    background-color: #fef2f2;
                    border: 1px solid #fee2e2;
                    padding: 10px 14px;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    text-align: center;
                    margin-bottom: 16px;
                }



                @media (max-width: 768px) {
                    .account-drawer {
                        width: 100% !important;
                        min-width: unset !important;
                        border-radius: 0;
                    }
                    .account-header {
                        padding: 16px 20px !important;
                    }
                    .account-content {
                        padding: 16px !important;
                    }
                }
                `}
            </style>

            <div className="account-drawer" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="account-header">
                    <div className="account-title-container">
                        <AiOutlineUser size={22} color="#43c8ca" />
                        <h2 className="account-title">Account</h2>
                    </div>
                    <button onClick={onClose} className="account-close-btn" aria-label="Close menu">
                        <AiOutlineClose size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="account-content">
                    {!isAuth ? (
                        <div className="login-card">
                            {loginStep === 'welcome' && (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '19px', fontWeight: 800, marginBottom: '8px', color: '#1f2937', letterSpacing: '-0.3px' }}>Welcome to HoneyCool</div>
                                    <div style={{ fontSize: '13.5px', color: '#6b7280', marginBottom: '24px' }}>Login to access your orders & wishlist</div>

                                    <button
                                        onClick={() => setLoginStep('phone')}
                                        className="login-action-btn"
                                    >
                                        Login with OTP
                                    </button>
                                </div>
                            )}

                            {loginStep === 'phone' && (
                                <form onSubmit={handleSendOtp} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#1f2937', marginBottom: '4px' }}>Enter Mobile Number</div>
                                        <div style={{ fontSize: '13px', color: '#6b7280' }}>We'll send an OTP to verify your account</div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Mobile Number *</label>
                                        <div className="input-wrapper">
                                            <div className="input-prefix">+91</div>
                                            <input
                                                type="tel"
                                                required
                                                maxLength={10}
                                                value={mobileNumber}
                                                onChange={e => setMobileNumber(e.target.value.replace(/\D/g, ''))}
                                                placeholder="Enter 10-digit mobile"
                                                className="form-input"
                                                autoFocus
                                            />
                                        </div>
                                    </div>

                                    {loginError && <div className="error-box">{loginError}</div>}

                                    <button
                                        type="submit"
                                        disabled={loginLoading}
                                        className="login-action-btn"
                                        style={{ marginBottom: '16px' }}
                                    >
                                        {loginLoading ? 'Sending OTP...' : 'Send OTP'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setLoginStep('welcome')}
                                        style={{
                                            background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer',
                                            fontSize: '13px', fontWeight: 700, alignSelf: 'center'
                                        }}
                                    >
                                        Go Back
                                    </button>
                                </form>
                            )}

                            {loginStep === 'otp' && (
                                <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#1f2937', marginBottom: '4px' }}>Verify OTP</div>
                                        <div style={{ fontSize: '13px', color: '#6b7280' }}>Sent to +91 {mobileNumber}</div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Enter 4-Digit OTP *</label>
                                        <input
                                            type="text"
                                            required
                                            maxLength={4}
                                            value={otpValue}
                                            onChange={e => setOtpValue(e.target.value.replace(/\D/g, ''))}
                                            placeholder="••••"
                                            className="otp-input"
                                            autoFocus
                                        />
                                        <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', marginTop: '6px', fontWeight: 500 }}>
                                            Stuck? Enter <strong>1234</strong> to login instantly!
                                        </div>
                                    </div>

                                    {loginError && <div className="error-box">{loginError}</div>}

                                    <button
                                        type="submit"
                                        disabled={loginLoading}
                                        className="login-action-btn"
                                        style={{ marginBottom: '16px' }}
                                    >
                                        {loginLoading ? 'Verifying...' : 'Verify & Login'}
                                    </button>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setLoginStep('phone')}
                                            style={{
                                                background: 'none', border: 'none', color: '#43c8ca', cursor: 'pointer',
                                                fontSize: '13px', fontWeight: 700
                                            }}
                                        >
                                            Change Number
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSendOtp}
                                            style={{
                                                background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer',
                                                fontSize: '13px', fontWeight: 700
                                            }}
                                        >
                                            Resend OTP
                                        </button>
                                    </div>
                                </form>
                            )}

                            {loginStep === 'register' && (
                                <div>
                                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '19px', fontWeight: 800, color: '#1f2937', marginBottom: '4px' }}>Complete Profile</div>
                                        <div style={{ fontSize: '13px', color: '#6b7280' }}>Fill in details to finish setting up your account.</div>
                                    </div>

                                    <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label">Mobile Number</label>
                                            <div className="input-wrapper" style={{ opacity: 0.8 }}>
                                                <div className="input-prefix">+91</div>
                                                <input
                                                    type="tel"
                                                    value={mobileNumber}
                                                    readOnly
                                                    className="form-input"
                                                    style={{ cursor: 'not-allowed' }}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label">First Name *</label>
                                            <input
                                                type="text"
                                                required
                                                value={regFirstName}
                                                onChange={e => setRegFirstName(e.target.value)}
                                                placeholder="Enter first name"
                                                className="otp-input"
                                                style={{ textAlign: 'left', letterSpacing: 'normal', fontSize: '14px' }}
                                            />
                                        </div>

                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label">Last Name</label>
                                            <input
                                                type="text"
                                                value={regLastName}
                                                onChange={e => setRegLastName(e.target.value)}
                                                placeholder="Enter last name (optional)"
                                                className="otp-input"
                                                style={{ textAlign: 'left', letterSpacing: 'normal', fontSize: '14px' }}
                                            />
                                        </div>

                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label">Email *</label>
                                            <input
                                                type="email"
                                                required
                                                value={regEmail}
                                                onChange={e => setRegEmail(e.target.value)}
                                                placeholder="Enter email address"
                                                className="otp-input"
                                                style={{ textAlign: 'left', letterSpacing: 'normal', fontSize: '14px' }}
                                            />
                                        </div>

                                        {regError && <div className="error-box">{regError}</div>}

                                        <button
                                            type="submit"
                                            disabled={regLoading}
                                            className="login-action-btn"
                                            style={{ marginTop: '6px' }}
                                        >
                                            {regLoading ? 'Registering...' : 'Complete Registration'}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => { setLoginStep('otp'); setRegError(''); }}
                                            style={{
                                                background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer',
                                                fontSize: '13px', fontWeight: 700, alignSelf: 'center', marginTop: '4px'
                                            }}
                                        >
                                            ← Back to OTP
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="profile-banner">
                            <div className="profile-avatar">
                                {name?.firstname?.[0] || 'U'}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                                <div className="profile-name">
                                    {name?.firstname || 'User'} {name?.lastname || ''}
                                </div>
                                <div className="profile-email">
                                    {name?.email || 'user@example.com'}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="account-menu-list">
                        {/* Orders */}
                        <div className="account-menu-item" onClick={() => {
                            onClose();
                            if (onOpenOrders) {
                                onOpenOrders();
                            }
                        }}>
                            <MdOutlineCardGiftcard className="account-menu-icon" />
                            <span className="account-menu-label">Orders</span>
                        </div>

                        {/* Mobile Only Bag */}
                        <div className="account-menu-item mobile-only-menu-item" onClick={() => {
                            onClose();
                            if (onOpenCart) {
                                onOpenCart();
                            }
                        }}>
                            <AiOutlineShoppingCart className="account-menu-icon" />
                            <span className="account-menu-label">Bag</span>
                            {cartCount > 0 && (
                                <span className="account-menu-badge teal">{cartCount}</span>
                            )}
                        </div>

                        {/* Mobile Only Wishlist */}
                        {!isAuth && (
                            <div className="account-menu-item mobile-only-menu-item" onClick={() => {
                                onClose();
                                if (onOpenWishlist) {
                                    onOpenWishlist();
                                }
                            }}>
                                <AiOutlineHeart className="account-menu-icon" />
                                <span className="account-menu-label">Wishlist</span>
                                {wishlistCount > 0 && (
                                    <span className="account-menu-badge">{wishlistCount}</span>
                                )}
                            </div>
                        )}

                        {/* Addresses */}
                        {isAuth && (
                            <div className="account-menu-item" onClick={() => setIsAddressModalOpen(true)}>
                                <AiOutlineHome className="account-menu-icon" />
                                <span className="account-menu-label">Address</span>
                            </div>
                        )}

                        {/* Settings */}
                        {isAuth && (
                            <div className="account-menu-item" onClick={() => {
                                onClose();
                                if (onOpenSettings) {
                                    onOpenSettings();
                                }
                            }}>
                                <MdOutlineSettings className="account-menu-icon" />
                                <span className="account-menu-label">Settings</span>
                            </div>
                        )}

                        {/* Logout */}
                        {isAuth && (
                            <div
                                className="account-menu-item"
                                style={{ color: '#ef4444' }}
                                onClick={() => {
                                    dispatch(logout());
                                    toast.success("Logged out successfully!");
                                    navigate('/');
                                    onClose();
                                }}
                            >
                                <MdLogout className="account-menu-icon" style={{ color: '#ef4444' }} />
                                <span className="account-menu-label">Logout</span>
                            </div>
                        )}
                    </div>
                </div>


            </div>
            <AddressModal isOpen={isAddressModalOpen} onClose={() => setIsAddressModalOpen(false)} />
        </div>
    );
};

export default AccountModal;
